import { useState, useEffect, useCallback } from "react";
import { eventsApi, toDE } from "../api";
import type { EventCreateRequest, UpdateEventRequest, ResultRequest } from "../api/events";
import { DartsEvent } from "../types";
import { useToast } from "../context/ToastContext";

interface EventsState {
  events: DartsEvent[];
  isLoading: boolean;
  error: string | null;
}

export const useEvents = () => {
  const { showToast } = useToast();
  const [state, setState] = useState<EventsState>({
    events: [],
    isLoading: true,
    error: null,
  });

  const fetchEvents = useCallback(async () => {
    setState((s) => ({ ...s, isLoading: true, error: null }));
    try {
      const raw = await eventsApi.getAll();
      setState({ events: raw.map(toDE), isLoading: false, error: null });
    } catch {
      setState((s) => ({
        ...s,
        isLoading: false,
        error: "イベントの取得に失敗しました",
      }));
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const createEvent = useCallback(
    async (body: EventCreateRequest): Promise<boolean> => {
      try {
        await eventsApi.create(body);
        await fetchEvents();
        showToast("イベントを作成しました。", "success");
        return true;
      } catch {
        showToast("イベントの作成に失敗しました。", "error");
        return false;
      }
    },
    [showToast, fetchEvents]
  );

  const updateEvent = useCallback(
    async (id: string, body: UpdateEventRequest): Promise<boolean> => {
      try {
        await eventsApi.update(id, body);
        await fetchEvents();
        showToast("イベントを更新しました。", "success");
        return true;
      } catch {
        showToast("イベントの更新に失敗しました。", "error");
        return false;
      }
    },
    [showToast, fetchEvents]
  );

  const removeEvent = useCallback(async (id: string): Promise<boolean> => {
    try {
      await eventsApi.remove(id);
      setState((s) => ({
        ...s,
        events: s.events.filter((e) => e.id !== id),
      }));
      showToast("イベントを削除しました。", "success");
      return true;
    } catch {
      showToast("イベントの削除に失敗しました。", "error");
      return false;
    }
  }, [showToast]);

  const startEvent = useCallback(async (eventId: string): Promise<boolean> => {
    try {
      await eventsApi.start(eventId);
      await fetchEvents();
      return true;
    } catch {
      showToast("イベントの開始に失敗しました。", "error");
      return false;
    }
  }, [showToast, fetchEvents]);

  const registerResult = useCallback(
    async (eventId: string, body: ResultRequest): Promise<boolean> => {
      try {
        await eventsApi.registerResult(eventId, body);
        return true;
      } catch {
        return false;
      }
    },
    []
  );

  const endEvent = useCallback(async (eventId: string): Promise<boolean> => {
    try {
      await eventsApi.end(eventId);
      await fetchEvents();
      return true;
    } catch {
      return false;
    }
  }, [fetchEvents]);

  const entryEvent = useCallback(
    async (eventId: string): Promise<boolean> => {
      try {
        await eventsApi.entry(eventId);
        return true;
      } catch {
        return false;
      }
    },
    []
  );

  const cancelEntry = useCallback(
    async (eventId: string): Promise<boolean> => {
      try {
        await eventsApi.cancelEntry(eventId);
        return true;
      } catch {
        return false;
      }
    },
    []
  );

  return {
    ...state,
    fetchEvents,
    createEvent,
    updateEvent,
    removeEvent,
    startEvent,
    registerResult,
    endEvent,
    entryEvent,
    cancelEntry,
  };
};
